import { useState, useEffect, useRef } from "react";
import AssessmentQuestion from "@/components/AssessmentQuestion";
import { useFieldArray, useFormContext } from "react-hook-form";
import Button from "@/components/Button";
import { QuestionType } from "@/components/AssessmentQuestion/types";

import { Reorder, useDragControls } from "framer-motion";
import classNames from "classnames";
import { ApplicationQuestion, Option } from "@/pages/_layouts/jobs/types";
import QuestionsDetailsReadOnly from "./QuestionsDetailsReadOnly";

const DEFAULT_QUESTION_TYPE = QuestionType.OPEN_TEXT;
const randomNum = () => (Math.random() * 10000000).toFixed(0);

function DraggableQuestion({
  question,
  index,
  focusIndex,
  setFocusIndex,
  handleQuestionChange,
  handleQuestionTypeChange,
  handleOptionsChange,
  containerRef,
  handleDeleteQuestion
}: {
  question: ApplicationQuestion;
  index: number;
  focusIndex: number;
  setFocusIndex: (index: number) => void;
  handleQuestionChange: (index: number, newQuestion: string) => void;
  handleQuestionTypeChange: (index: number, newQuestionType: QuestionType) => void;
  handleOptionsChange: (index: number, newOptions: any) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  handleDeleteQuestion: (index: number) => void;
}) {
  const dragControls = useDragControls();
  const [wasDragged, setWasDragged] = useState(false);

  const handleDragStart = () => {
    setFocusIndex(-1);
    setWasDragged(true);
  };

  const handleDragEnd = () => {
    setTimeout(() => setWasDragged(false), 0);
  };

  const activeStyle = {
    backgroundColor: "var(--sys-brand-surface-container)",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    opacity: 0.8,
    border: "1px solid var(--sys-brand-outline-variant)",
    borderRadius: "16px",
    zIndex: 1000
  };

  return (
    <Reorder.Item
      key={question.id}
      value={question}
      dragControls={dragControls}
      dragListener={false}
      layout
      transition={{ duration: 0 }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      dragConstraints={containerRef}
      dragElastic={0.1}
      whileDrag={activeStyle}
      style={{ position: "relative" }}
    >
      <div
        data-testid={`custom-question-${index}`}
        className={classNames("my-6", {
          "cursor-pointer": focusIndex !== index
        })}
        onClick={(e) => {
          e.stopPropagation();
          if (!wasDragged && !(e.target as HTMLElement).closest(".drag-handle")) {
            setFocusIndex(index);
          }
        }}
      >
        <AssessmentQuestion
          position={index}
          isFocused={focusIndex === index}
          question={question.content}
          setQuestion={(newQuestion) => handleQuestionChange(index, newQuestion)}
          questionType={question.questionType}
          setQuestionType={(newQuestionType) => handleQuestionTypeChange(index, newQuestionType)}
          questionOptions={question.options.map((option: any, optIndex: number) => ({
            position: optIndex,
            text: option.content
          }))}
          setQuestionOptions={(newOptions) => handleOptionsChange(index, newOptions)}
          onDragHandlePointerDown={(event) => {
            dragControls.start(event);
            handleDragStart();
          }}
          handleDeleteQuestion={handleDeleteQuestion}
        />
      </div>
    </Reorder.Item>
  );
}

export default function QuestionDetails({ form }: { form: any }) {
  const { setValue, watch, control } = useFormContext();
  const isEditable = !(form.watch("application.isFormLocked") || false);

  const { append, remove } = useFieldArray({
    control,
    name: "application.questions"
  });
  const [focusIndex, setFocusIndex] = useState<number>(0);
  const pageRef = useRef<HTMLDivElement>(null);
  const questionsData = form?.formState?.defaultValues?.application?.questions;
  const [questions, setQuestions] = useState<ApplicationQuestion[]>(questionsData?.questions || []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pageRef.current && !pageRef.current.contains(event.target as Node)) {
        setFocusIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    questions.forEach((question, index) => {
      setValue(
        `application.questions[${index}]`,
        {
          id: question.id,
          content: question.content,
          options: question.options.map((option) => ({
            id: option.id,
            content: option.content
          })),
          questionType: question.questionType
        },
        { shouldDirty: false }
      );
    });
  }, [questions, setValue]);

  const formQuestions = watch("application.questions");

  useEffect(() => {
    if (Array.isArray(formQuestions)) {
      setQuestions(
        formQuestions.map((question: ApplicationQuestion) => {
          return {
            id: question.id,
            content: question.content,
            options: Array.isArray(question.options)
              ? question.options.map((option: Option) => ({
                  id: option.id,
                  content: option.content
                }))
              : [],
            questionType: question.questionType
          };
        })
      );
    }
  }, [formQuestions]);

  const handleQuestionChange = (index: number, newQuestion: string) => {
    setQuestions((prevQuestions) => {
      const updatedQuestions = [...prevQuestions];
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        content: newQuestion
      };
      return updatedQuestions;
    });
    setValue(`application.questions[${index}].content`, newQuestion, { shouldDirty: true });
  };

  const handleQuestionTypeChange = (index: number, newQuestionType: QuestionType) => {
    setQuestions((prevQuestions) => {
      const updatedQuestions = [...prevQuestions];
      updatedQuestions[index] = { ...updatedQuestions[index], questionType: newQuestionType };
      return updatedQuestions;
    });
  };

  const handleOptionsChange = (index: number, newOptions: any) => {
    setQuestions((prevQuestions) => {
      const updatedQuestions = [...prevQuestions];
      updatedQuestions[index] = {
        ...updatedQuestions[index],
        options: newOptions.map((option: any) => ({
          content: option.text
        }))
      };
      return updatedQuestions;
    });
    setValue(
      `application.questions[${index}].options`,
      newOptions.map((option: any) => ({
        content: option.text
      })),
      { shouldDirty: true }
    );
  };
  const handleAddQuestion = () => {
    const newId = randomNum();
    append({
      content: "",
      id: `new-question-${newId}`,
      options: [],
      questionType: DEFAULT_QUESTION_TYPE
    });
    setFocusIndex(questions.length);
  };

  const handleDeleteQuestion = (index: number) => {
    remove(Number(index));
  };

  if (!isEditable) {
    return <QuestionsDetailsReadOnly questions={questions} />;
  }

  return (
    <>
      <div className="col-span-full">
        <div ref={pageRef}>
          <Reorder.Group
            axis="y"
            values={questions}
            onReorder={setQuestions}
            layoutScroll
            style={{ overflow: "visible", width: "100%" }}
          >
            {questions.map((question, index) => (
              <DraggableQuestion
                key={question.id}
                question={question}
                index={index}
                focusIndex={focusIndex}
                setFocusIndex={setFocusIndex}
                handleQuestionChange={handleQuestionChange}
                handleQuestionTypeChange={handleQuestionTypeChange}
                handleOptionsChange={handleOptionsChange}
                containerRef={pageRef}
                handleDeleteQuestion={handleDeleteQuestion}
              />
            ))}
          </Reorder.Group>
        </div>
      </div>
      <Button variant="filledTonal" icon="add" onClick={handleAddQuestion}>
        Add Question
      </Button>
    </>
  );
}
